import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function EducationZakat() {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState('pengertian');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'pengertian',
      number: '01',
      title: 'Pengertian & Makna Zakat',
      summary: 'Makna zakat secara bahasa dan istilah, kedudukan rukun Islam, serta tujuan sosial-spiritualnya.',
      content: (
        <div className="space-y-5">
          <p className="text-xs sm:text-sm leading-relaxed text-slate-700">
            Secara bahasa, <strong className="text-blue-900">zakat</strong> bermakna tumbuh, suci, dan berkembang. Secara istilah syariat, zakat adalah bagian tertentu dari harta yang wajib dikeluarkan oleh seorang Muslim yang telah memenuhi syarat nishab dan haul untuk disalurkan kepada golongan yang berhak.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4 space-y-1">
              <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider">Penyucian Harta & Jiwa</h4>
              <p className="text-xs leading-relaxed text-slate-600">
                Zakat merupakan ibadah maliyah yang membersihkan harta dari hak sesama serta melatih keikhlasan.
              </p>
            </div>
            <div className="rounded-xl border border-amber-200/80 bg-amber-50/40 p-4 space-y-1">
              <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wider">Rukun Islam Ketiga</h4>
              <p className="text-xs leading-relaxed text-slate-600">
                Kedudukannya sangat fundamental dalam ajaran Islam, menjaga keseimbangan ibadah dan kepedulian sosial.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-blue-100 bg-white p-4 space-y-3">
            <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider">Tujuan Utama Zakat</h4>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                'Membersihkan jiwa donatur dari sifat kikir dan tamak.',
                'Meringankan beban fakir, miskin, dan golongan yang membutuhkan.',
                'Mewujudkan keadilan serta keharmonisan sosial.',
                'Menguatkan ukhuwah antar sesama umat Muslim.',
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-xs text-slate-700">
                  <span className="text-blue-900 font-bold">•</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'jenis-zakat',
      number: '02',
      title: 'Jenis-Jenis Zakat',
      summary: 'Mengenal ketentuan zakat mal (harta/penghasilan) dan zakat fitrah.',
      content: (
        <div className="space-y-5">
          <p className="text-xs sm:text-sm leading-relaxed text-slate-700">
            Kewajiban zakat terbagi menjadi dua kategori utama, yaitu <strong className="text-blue-900">Zakat Fitrah</strong> (dikeluarkan pada bulan Ramadan) dan <strong className="text-blue-900">Zakat Mal</strong> (dikeluarkan atas aset/penghasilan yang memenuhi syarat).
          </p>

          <div className="grid gap-4 xl:grid-cols-2">
            <section className="rounded-xl border border-blue-100 bg-blue-50/40 p-4 space-y-2">
              <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-900 border border-blue-200">
                HARTA & PENGHASILAN
              </span>
              <h4 className="text-sm font-bold text-blue-900">Zakat Mal</h4>
              <p className="text-xs text-slate-600">
                Dikenakan atas aset atau pendapatan yang mencapai batas nishab dan memenuhi haul (1 tahun).
              </p>
              <div className="grid grid-cols-2 gap-2 pt-1">
                {['Emas & Perak', 'Uang & Tabungan', 'Penghasilan Profesi', 'Perdagangan / Bisnis', 'Hasil Pertanian', 'Hewan Ternak'].map((item) => (
                  <div key={item} className="rounded-lg border border-slate-100 bg-white px-3 py-2 text-xs font-semibold text-blue-900 text-center">
                    {item}
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-xl border border-amber-200/80 bg-amber-50/40 p-4 space-y-2">
              <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-200">
                PENYUCIAN DIRI RAMADAN
              </span>
              <h4 className="text-sm font-bold text-blue-900">Zakat Fitrah</h4>
              <p className="text-xs text-slate-600">
                Ditunaikan sebelum shalat Idulfitri sebagai bentuk penyucian jiwa dan berbagi kepada sesama.
              </p>
              <div className="space-y-2 pt-1">
                {['Wajib bagi setiap Muslim yang mampu', 'Sebesar 2,5 kg / 3,5 liter makanan pokok (atau nilai setara)', 'Disalurkan kepada fakir miskin di bulan Ramadan'].map((item) => (
                  <div key={item} className="rounded-lg border border-slate-100 bg-white px-3 py-2 text-xs text-slate-700 flex items-center gap-2">
                    <span className="text-amber-600 font-bold">•</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      ),
    },
    {
      id: 'dalil',
      number: '03',
      title: 'Dalil Al-Quran & Hadits',
      summary: 'Landasan hukum kewajiban zakat berdasarkan firman Allah SWT dan sabda Rasulullah SAW.',
      content: (
        <div className="space-y-5">
          <section className="space-y-3">
            <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider">Dalil Al-Quran</h4>
            <div className="space-y-3">
              {[
                { source: 'QS. At-Taubah (9): 103', text: 'Ambillah zakat dari sebagian harta mereka, dengan zakat itu kamu membersihkan dan mensucikan mereka...' },
                { source: 'QS. Al-Baqarah (2): 43', text: 'Dan dirikanlah shalat, tunaikanlah zakat, dan rukuklah beserta orang-orang yang rukuk.' },
                { source: 'QS. At-Taubah (9): 60', text: 'Sesungguhnya zakat-zakat itu hanyalah untuk orang-orang fakir, miskin, amil, muallaf, riqab, gharim, fi sabilillah, dan ibnu sabil.' },
              ].map((item) => (
                <div key={item.source} className="rounded-xl border border-blue-100 bg-white p-4 shadow-sm space-y-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">{item.source}</p>
                  <p className="text-xs leading-relaxed text-slate-700 italic">"{item.text}"</p>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-3 pt-1">
            <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider">Dalil Hadits</h4>
            <div className="space-y-3">
              {[
                { source: 'HR. Bukhari & Muslim', text: 'Islam dibangun atas lima perkara: bersaksi tidak ada tuhan selain Allah, mendirikan shalat, menunaikan zakat, berpuasa Ramadan, dan haji.' },
                { source: 'HR. Muslim', text: 'Harta tidak akan berkurang karena sedekah...' },
              ].map((item) => (
                <div key={item.source} className="rounded-xl border border-blue-100 bg-white p-4 shadow-sm space-y-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700">{item.source}</p>
                  <p className="text-xs leading-relaxed text-slate-700 italic">"{item.text}"</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      ),
    },
    {
      id: 'doa',
      number: '04',
      title: 'Doa Niat & Penyaluran Zakat',
      summary: 'Teks bahasa Arab, transliterasi Latin, dan terjemahan doa zakat, wakaf, dan infak.',
      content: (
        <div className="space-y-4">
          {[
            {
              title: 'Doa Menunaikan Zakat',
              arabic: 'اللَّهُمَّ تَقَبَّلْ مِنِّي وَتَقَبَّلْ مِنَّا وَاجْعَلْهُ لَنَا شَاهِدًا وَلاَ جَارًّا',
              latin: 'Allahumma taqabbal minni wa taqabbal minna waj‘alhu lana shahidan wa la jarran.',
              translation: 'Ya Allah, terimalah zakatku dan zakat kami, jadikanlah untuk kami sebagai saksi kebaikan dan bukan sebagai beban.',
            },
            {
              title: 'Doa Wakaf',
              arabic: 'رَبَّنَا تَقَبَّلْ مِنَّا إِنَّكَ أَنتَ السَّمِيعُ الْعَلِيمُ',
              latin: 'Rabbana taqabbal minna innaka antas-sami‘ul-‘alim.',
              translation: 'Ya Tuhan kami, terimalah dari kami, sesungguhnya Engkaulah Yang Maha Mendengar lagi Maha Mengetahui.',
            },
            {
              title: 'Doa Infak & Sedekah',
              arabic: 'اللَّهُمَّ أَعِنِّي عَلَىٰ ذِكْرِكَ وَشُكْرِكَ وَحُسْنِ عِبَادَتِكَ',
              latin: 'Allahumma a‘inni ‘ala dhikrika wa shukrika wa husni ‘ibadatik.',
              translation: 'Ya Allah, tolonglah aku untuk selalu mengingat-Mu, bersyukur kepada-Mu, dan beribadah dengan baik kepada-Mu.',
            },
          ].map((item) => (
            <div key={item.title} className="rounded-xl border border-blue-100 bg-white p-4 shadow-sm space-y-2">
              <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-200">
                {item.title}
              </span>
              <p className="text-right text-base sm:text-lg font-bold text-blue-950 py-1 font-serif leading-loose">{item.arabic}</p>
              <p className="text-xs text-blue-900 font-semibold"><span className="text-slate-400 font-normal">Latin:</span> {item.latin}</p>
              <p className="text-xs text-slate-600"><span className="text-slate-400 font-normal">Terjemahan:</span> {item.translation}</p>
            </div>
          ))}
        </div>
      ),
    },
    {
      id: 'penerima',
      number: '05',
      title: '8 Golongan Asnaf Penerima Zakat',
      summary: 'Penerima zakat yang sah menurut surat At-Taubah ayat 60.',
      content: (
        <div className="space-y-4">
          <p className="text-xs sm:text-sm leading-relaxed text-slate-700">
            Penyaluran zakat dikelola secara amanah untuk 8 golongan (*asnaf*) penerima berikut:
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ['Fakir', 'Orang yang tidak memiliki harta dan penghasilan untuk kebutuhan dasar.'],
              ['Miskin', 'Orang yang memiliki penghasilan namun belum mencukupi kebutuhan pokok yang layak.'],
              ['Amil', 'Pengelola dan lembaga resmi yang bertugas menghimpun serta menyalurkan zakat.'],
              ['Muallaf', 'Orang yang baru masuk Islam atau yang dilunakkan hatinya dalam kebaikan.'],
              ['Riqab', 'Pembebasan hamba sahaya atau perlindungan dari perbudakan modern.'],
              ['Gharim', 'Orang yang memiliki utang untuk kebutuhan pokok yang dibenarkan dan tidak mampu melunasinya.'],
              ['Fi Sabilillah', 'Pejuang di jalan Allah untuk kemaslahatan dakwah, pendidikan, dan sosial umat.'],
              ['Ibnu Sabil', 'Musafir yang kehabisan bekal dalam perjalanan ketaatan.'],
            ].map(([title, desc], index) => (
              <div key={title} className="rounded-xl border border-blue-100 bg-white p-3.5 shadow-sm flex items-start gap-3">
                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-900 text-amber-400 text-xs font-bold flex-shrink-0">
                  {index + 1}
                </div>
                <div>
                  <h4 className="text-xs font-bold text-blue-900">{title}</h4>
                  <p className="mt-0.5 text-[11px] text-slate-600 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ];

  const activeSection = sections.find((section) => section.id === expandedSection) || sections[0];

  return (
    <div className="min-h-screen bg-slate-50 text-blue-900 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Header Section */}
        <div className="bg-white rounded-2xl border border-blue-100 p-6 md:p-8 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1 flex-1">
            <span className="inline-block text-[11px] font-semibold tracking-wider text-blue-700 uppercase bg-blue-50 px-3 py-1 rounded-full border border-blue-100">
              Panduan Syariat MPZ DT
            </span>
            <h1 className="text-2xl md:text-3xl font-bold text-blue-900 tracking-tight mt-1">
              Edukasi Zakat
            </h1>
            <p className="text-xs md:text-sm text-slate-600">
              Panduan fiqh zakat, dalil syariat, niat doa, dan ketentuan 8 golongan asnaf penerima zakat.
            </p>
          </div>
          <button
            onClick={() => navigate('/kalkulator-zakat')}
            className="px-4 py-2.5 rounded-xl bg-blue-900 hover:bg-blue-800 text-white text-xs font-bold shadow-sm transition self-start md:self-center"
          >
            Hitung Zakat Anda
          </button>
        </div>

        {/* Content Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Kolom Kiri: Menu Topik (5 Cols) */}
          <div className="lg:col-span-5 space-y-4">
            <div className="bg-white rounded-2xl border border-blue-100 p-5 shadow-sm space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-blue-50">
                <h2 className="text-xs font-bold uppercase tracking-wider text-blue-900">Daftar Materi</h2>
                <span className="text-[10px] font-semibold bg-blue-50 text-blue-800 px-2 py-0.5 rounded-full border border-blue-100">5 Topik</span>
              </div>

              <div className="space-y-2">
                {sections.map((section) => {
                  const isActive = section.id === expandedSection;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => toggleSection(section.id)}
                      className={`w-full text-left p-3.5 rounded-xl border transition-all flex items-start gap-3 ${
                        isActive
                          ? 'border-blue-900 bg-blue-900 text-white shadow-sm'
                          : 'border-blue-100 bg-slate-50/50 text-slate-700 hover:border-blue-300 hover:bg-white'
                      }`}
                    >
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${
                        isActive ? 'bg-amber-400 text-blue-950' : 'bg-slate-200 text-slate-700'
                      }`}>
                        {section.number}
                      </span>
                      <div className="space-y-0.5 flex-1 min-w-0">
                        <p className={`text-xs font-bold ${isActive ? 'text-white' : 'text-blue-900'}`}>{section.title}</p>
                        <p className={`text-[11px] line-clamp-2 leading-relaxed font-normal ${isActive ? 'text-blue-100' : 'text-slate-500'}`}>
                          {section.summary}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Banner Aksi */}
            <div className="bg-white rounded-2xl border border-blue-100 p-5 shadow-sm space-y-3 text-center sm:text-left">
              <p className="text-xs font-bold text-blue-900">Tunaikan Zakat Bersama MPZ DT</p>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                Penyaluran zakat dikelola secara amanah, profesional, dan transparan untuk penerima manfaat yang berhak.
              </p>
              <button
                onClick={() => navigate('/donasi/zakat')}
                className="w-full py-2.5 rounded-xl bg-amber-400 hover:bg-amber-300 text-blue-950 text-xs font-bold shadow-sm transition"
              >
                Tunaikan Donasi Zakat
              </button>
            </div>
          </div>

          {/* Kolom Kanan: Detail Topik Aktif (7 Cols) */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl border border-blue-100 p-6 md:p-8 shadow-sm space-y-5">
              <div className="pb-3 border-b border-blue-50">
                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded border border-blue-100">
                  Materi #{activeSection.number}
                </span>
                <h2 className="text-lg md:text-xl font-bold text-blue-900 mt-2">{activeSection.title}</h2>
              </div>

              {/* Dynamic Content */}
              <div className="pt-1">
                {activeSection.content}
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}

export default EducationZakat;
